import React, { useState } from "react";
import { Loader2, Check } from "lucide-react";
import { api } from "../../lib/api";
import { withAttribution, getUtm } from "../../lib/utm";
import { trackEvent } from "../../lib/track";

const INTEREST_OPTIONS = [
  { value: "frequency_snapshot", label: "Take the Clarity Snapshot" },
  { value: "orientation_reset", label: "Book the Clarity Reset Session" },
  { value: "blueprint", label: "Explore the Personal Action Plan" },
  { value: "book", label: "Read the Book" },
  { value: "not_sure", label: "Not sure yet, just exploring" },
];

const NEXT_STEP_OPTIONS = [
  { value: "snapshot", label: "Send me the Clarity Snapshot link" },
  { value: "schedule_call", label: "I want to book a call" },
  { value: "stay_in_touch", label: "Just keep me in the loop" },
];

/**
 * LeadCaptureForm
 *
 * A grounded, low-friction lead form for visitors who are interested
 * but not yet ready to buy or book. Captures the bare minimum (email)
 * plus optional context fields so Cyrus can respond with the right
 * next step instead of a generic newsletter. UTM data captured on
 * arrival is automatically attached to the submission so we can see
 * which channels (LinkedIn, TikTok, referral) actually convert.
 *
 * The form intentionally does NOT promise a specific response time
 * or use urgency language. Confirmation copy is warm and clear.
 */
const LeadCaptureForm = ({
  source = "site",
  defaultInterest = "",
  eyebrow = "Soft landing · Stay in the signal",
  headline = "Not ready yet? That's fine.",
  subhead = "Leave your name and a sentence about where you are. I'll send the right next step when it makes sense.",
  className = "",
  testidPrefix = "lead-form",
}) => {
  const [state, setState] = useState({ phase: "idle", err: "" });
  const [form, setForm] = useState({
    name: "",
    email: "",
    moving_through: "",
    interest: defaultInterest,
    preferred_next_step: "",
    message: "",
  });

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state.phase === "submitting") return;
    // Lightweight client-side validation. The backend re-validates
    // via Pydantic so this is purely a UX nicety.
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setState({ phase: "error", err: "Please enter a valid email address." });
      return;
    }
    if (form.name && form.name.trim().length < 2) {
      setState({ phase: "error", err: "Your name looks too short to be real." });
      return;
    }
    setState({ phase: "submitting", err: "" });
    try {
      const payload = withAttribution({
        name: form.name.trim() || null,
        email: form.email.trim(),
        moving_through: form.moving_through.trim() || null,
        interest: form.interest || null,
        preferred_next_step: form.preferred_next_step || null,
        message: form.message.trim() || null,
        source,
      });
      await api.post("/leads", payload);
      trackEvent("lead_form_submitted", {
        source,
        interest: form.interest || null,
        preferred_next_step: form.preferred_next_step || null,
        attribution: getUtm(),
      });
      setState({ phase: "success", err: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something didn't land. Please try again.";
      setState({ phase: "error", err: typeof detail === "string" ? detail : "Submission failed." });
    }
  };

  if (state.phase === "success") {
    return (
      <div
        data-testid={`${testidPrefix}-success`}
        className={`glass-card p-8 md:p-10 text-center ${className}`}
      >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-gold/40 mb-5">
          <Check className="text-gold" size={20} />
        </div>
        <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-4">
          Received
        </p>
        <h3 className="font-serif text-2xl md:text-3xl text-themed leading-tight">
          Thank you for sharing where you are.
        </h3>
        <p className="mt-5 text-themed-muted leading-relaxed max-w-md mx-auto">
          Cyrus reads every submission personally. If your note calls for a direct reply, you'll hear back. Otherwise, the next aligned signal will arrive when it should.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={testidPrefix}
      className={`glass-card p-7 md:p-10 ${className}`}
      noValidate
    >
      <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-4">
        {eyebrow}
      </p>
      <h3 className="font-serif text-2xl md:text-3xl text-themed leading-tight font-light">
        {headline}
      </h3>
      <p className="mt-4 text-themed-muted leading-relaxed text-sm md:text-base">
        {subhead}
      </p>

      <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[10px] tracking-[0.3em] uppercase text-themed-muted mb-2 block">
            Name (optional)
          </span>
          <input
            type="text"
            value={form.name}
            onChange={update("name")}
            data-testid={`${testidPrefix}-name`}
            className="w-full bg-transparent border border-gold/15 focus:border-gold/60 px-4 py-3 text-themed placeholder-themed-muted/50 outline-none transition-colors"
            placeholder="First name"
            autoComplete="given-name"
          />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.3em] uppercase text-themed-muted mb-2 block">
            Email <span className="text-gold/80">*</span>
          </span>
          <input
            type="email"
            value={form.email}
            onChange={update("email")}
            data-testid={`${testidPrefix}-email`}
            required
            className="w-full bg-transparent border border-gold/15 focus:border-gold/60 px-4 py-3 text-themed placeholder-themed-muted/50 outline-none transition-colors"
            placeholder="you@domain.com"
            autoComplete="email"
          />
        </label>
      </div>

      <label className="block mt-5">
        <span className="text-[10px] tracking-[0.3em] uppercase text-themed-muted mb-2 block">
          What are you currently moving through?
        </span>
        <textarea
          value={form.moving_through}
          onChange={update("moving_through")}
          data-testid={`${testidPrefix}-moving-through`}
          rows={3}
          className="w-full bg-transparent border border-gold/15 focus:border-gold/60 px-4 py-3 text-themed placeholder-themed-muted/50 outline-none transition-colors resize-none"
          placeholder="A season, a decision, a stuck point. One or two sentences is enough."
        />
      </label>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[10px] tracking-[0.3em] uppercase text-themed-muted mb-2 block">
            Most interested in
          </span>
          <select
            value={form.interest}
            onChange={update("interest")}
            data-testid={`${testidPrefix}-interest`}
            className="w-full bg-transparent border border-gold/15 focus:border-gold/60 px-4 py-3 text-themed outline-none transition-colors"
          >
            <option value="">Choose one (optional)</option>
            {INTEREST_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="bg-[#0a0a0a] text-white">
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.3em] uppercase text-themed-muted mb-2 block">
            Preferred next step
          </span>
          <select
            value={form.preferred_next_step}
            onChange={update("preferred_next_step")}
            data-testid={`${testidPrefix}-next-step`}
            className="w-full bg-transparent border border-gold/15 focus:border-gold/60 px-4 py-3 text-themed outline-none transition-colors"
          >
            <option value="">Choose one (optional)</option>
            {NEXT_STEP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="bg-[#0a0a0a] text-white">
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block mt-5">
        <span className="text-[10px] tracking-[0.3em] uppercase text-themed-muted mb-2 block">
          Anything else worth sharing (optional)
        </span>
        <textarea
          value={form.message}
          onChange={update("message")}
          data-testid={`${testidPrefix}-message`}
          rows={2}
          className="w-full bg-transparent border border-gold/15 focus:border-gold/60 px-4 py-3 text-themed placeholder-themed-muted/50 outline-none transition-colors resize-none"
          placeholder="Skip if not relevant."
        />
      </label>

      {state.phase === "error" && (
        <p
          data-testid={`${testidPrefix}-error`}
          className="mt-5 text-sm text-rose-300/90"
        >
          {state.err}
        </p>
      )}

      <button
        type="submit"
        data-testid={`${testidPrefix}-submit`}
        disabled={state.phase === "submitting"}
        className="mt-7 btn-gold inline-flex items-center justify-center gap-2 w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {state.phase === "submitting" ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Sending
          </>
        ) : (
          "Send My Note"
        )}
      </button>

      <p className="mt-4 text-[11px] text-themed-muted/70 leading-relaxed">
        Your note goes directly to Cyrus. We don't use it for marketing automation. Unsubscribe anytime by replying.
      </p>
    </form>
  );
};

export default LeadCaptureForm;
